/**
 * AvatarVideo Component
 * Displays the HeyGen avatar video stream
 */

import { SessionState } from '@heygen/liveavatar-web-sdk';
import type React from 'react';
import { forwardRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStreamingAvatarContext } from '../context/StreamingAvatarContext';
import { useStreamingAvatarSession } from '../hooks/useStreamingAvatarSession';
import './AvatarVideo.css';

interface AvatarVideoProps {
  companionImage?: string;
  companionName?: string;
  isStarting?: boolean;
  onCancelStart?: () => void;
}

export const AvatarVideo = forwardRef<HTMLVideoElement, AvatarVideoProps>(
  ({ companionImage, companionName, isStarting = false, onCancelStart }, ref) => {
    const { t } = useTranslation();
    const { sessionState } = useStreamingAvatarSession();
    const { isAvatarTalking } = useStreamingAvatarContext();

    // Track whether video is actually playing (has visible content)
    const [isVideoPlaying, setIsVideoPlaying] = useState(false);

    const isLoaded = sessionState === SessionState.CONNECTED;
    const isConnecting = sessionState === SessionState.CONNECTING;
    const isInactive = sessionState === SessionState.INACTIVE;

    // Show loading during API call (isStarting), SDK connecting, or connected-but-buffering
    // However, if avatar is talking, assume video should be playing (hide loading overlay)
    const showLoading =
      isStarting || isConnecting || (isLoaded && !isVideoPlaying && !isAvatarTalking);
    const showIdle = isInactive && !isStarting;

    // Reset video playing state when session state changes
    useEffect(() => {
      if (sessionState !== SessionState.CONNECTED) {
        setIsVideoPlaying(false);
      }
    }, [sessionState]);

    // When avatar starts talking, ensure we show video and FORCE PLAY
    useEffect(() => {
      if (isAvatarTalking && sessionState === SessionState.CONNECTED) {
        // Force video to play and unmute
        if (ref && typeof ref !== 'function' && ref.current) {
          const video = ref.current;
          video.muted = false;
          video.volume = 1.0;

          const playPromise = video.play();
          if (playPromise !== undefined) {
            playPromise.catch(() => {
              // If auto-play fails, we might need user interaction
            });
          }
        }

        // Set a small timeout to allow video to start playing (visual state)
        const timer = setTimeout(() => {
          setIsVideoPlaying(true);
        }, 200);
        return () => clearTimeout(timer);
      }
    }, [isAvatarTalking, sessionState, ref]);

    // Global "Unmute Helper": Captures ANY interaction on the page to restore sound/play
    useEffect(() => {
      const attemptUnmute = () => {
        if (ref && typeof ref !== 'function' && ref.current) {
          const video = ref.current;
          // Only attempt if we have a stream (session connected)
          if (sessionState === SessionState.CONNECTED) {
            if (video.paused || video.muted) {
              video.muted = false;
              video.play().catch(() => {});
            }
          }
        }
      };

      document.addEventListener('click', attemptUnmute);
      document.addEventListener('touchstart', attemptUnmute);
      document.addEventListener('keydown', attemptUnmute);

      return () => {
        document.removeEventListener('click', attemptUnmute);
        document.removeEventListener('touchstart', attemptUnmute);
        document.removeEventListener('keydown', attemptUnmute);
      };
    }, [sessionState, ref]);

    // Handle video events
    const handleVideoPlaying = () => {
      setIsVideoPlaying(true);
    };

    const handleVideoWaiting = () => {
      // Video is buffering, might want to show loading
      // But don't set to false immediately to avoid flicker
    };

    const handleVideoError = (_e: React.SyntheticEvent<HTMLVideoElement, Event>) => {
      // Quietly handle error
    };

    return (
      <div className="avatar-video-container">
        {/* LIVE badge — shown when video is active */}
        {isLoaded && isVideoPlaying && (
          <div className="live-badge">
            <span className="live-badge-dot" />
            {t('chat.live', 'LIVE')}
          </div>
        )}

        {/* Video element */}
        <video
          ref={ref}
          autoPlay
          playsInline
          muted={false} // Explicitly unmuted
          className="avatar-video"
          onPlaying={handleVideoPlaying}
          onWaiting={handleVideoWaiting}
          onError={handleVideoError}
          onCanPlay={() => {
            // Video can play - ensure it's playing
            if (ref && typeof ref !== 'function' && ref.current) {
              const video = ref.current;
              if (video.paused) {
                video.play().catch((_err) => {});
              }
            }
          }}
          onLoadedData={() => {}}
        />

        {/* Starting Live Session animation — uses the golden moon MP4 video */}
        {showLoading && (
          <div className="avatar-loading-overlay">
            <div className="session-start-animation">
              {/* Background video — golden Ramadan moon animation */}
              <video
                className="session-start-bg-video"
                src="/vecteezy_golden-ramadan-kareem-moon-islamic-crescent-background_68703828.mp4"
                autoPlay
                loop
                muted
                playsInline
              />

              {/* Dark overlay for text readability */}
              <div className="session-start-overlay" />

              {/* Floating sparkle particles */}
              <div className="session-start-particles">
                <span className="sparkle sparkle-1" />
                <span className="sparkle sparkle-2" />
                <span className="sparkle sparkle-3" />
                <span className="sparkle sparkle-4" />
                <span className="sparkle sparkle-5" />
              </div>

              {/* Text */}
              <h3 className="session-start-title">
                {t('avatar.startingSession', 'Starting Live Session...')}
              </h3>
              <p className="session-start-subtitle">
                {t('avatar.connectingTo', 'Connecting to {{name}}...', {
                  name: companionName ?? 'Avatar',
                })}
              </p>

              {/* Cancel button */}
              {onCancelStart && (
                <button type="button" className="session-start-cancel" onClick={onCancelStart}>
                  {t('common.cancel', 'Cancel')}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Idle overlay - show when inactive (stopped) - just the image */}
        {showIdle && (
          <div className="avatar-loading-overlay">
            <div className="avatar-loading-content">
              {companionImage && (
                <img
                  src={companionImage}
                  alt={companionName || 'Avatar'}
                  className="avatar-loading-image"
                />
              )}
            </div>
          </div>
        )}
      </div>
    );
  },
);

AvatarVideo.displayName = 'AvatarVideo';
